import React, { useEffect, useRef, useState } from 'react'
import * as echarts from 'echarts'
import {
    WalletIcon,
    UserIcon,
    TicketIcon,
    UserGroupIcon,
    UsersIcon,
    UserCircleIcon,
    CheckCircleIcon,
    XCircleIcon,
    CheckBadgeIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/solid'
import { Select } from 'components/select'
import stSeat from '../../assets/images/icons/st_seat.svg'
import vipSeat from '../../assets/images/icons/vip_seat.svg'
import { DATE_RANGES, DateFilter } from '../dashboard/components/DateFilter'
import { useLazyQuery } from '@apollo/client'
import { DashboardSkeleton } from '../dashboard/components/DashboardSkeleton'
import { GraphQlResponseNoData } from 'components/custom/GraphQlResponseDisplay'
import i18next from 'i18next'
import { BookingsDashboardSkeleton } from '../dashboard/components/BookingsDashboardSkeleton'
import { useTranslation } from 'react-i18next'
import html2pdf from 'html2pdf.js'
import { PrinterIcon } from '@heroicons/react/24/outline'
import { GET_EXECUTIVE_DASHBOARD } from 'graphql/dashboard/queries'
import { BoatIcon } from 'components/icons'
import { StarIcon, DocumentTextIcon, BanknotesIcon } from '@heroicons/react/20/solid'
import PassengerTypeChart from 'pages/dashboard/components/PassengerTypeChart'
import PaymentMethodsChart from 'pages/dashboard/components/PaymentMethodsChart'

const StatCard = ({ title, value, type, icon, iconBg }) => (
    <div className="flex items-center justify-between bg-white p-4 py-2 rounded-lg shadow border border-gray-100">
        <div>
            <h3 className="text-gray-900 text-sm font-semibold">{title}</h3>
            <p className="text-lg font-bold text-primary">{value}</p>
        </div>
        <div className={`${iconBg || 'bg-primary'} py-2 px-2 rounded-xl flex items-center justify-center`}>{icon}</div>
    </div>
)

const ProgressBar = ({ label, percentage, ageRange }) => (
    <div className="mb-4">
        <div className="flex justify-between mb-1">
            <span className="text-gray-800 text-sm font-semibold">{label}</span>
            <span className="text-gray-600 text-md font-bold">{percentage}%</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded">
            <div
                className="h-full rounded"
                style={{
                    width: `${percentage}%`,
                    background: 'linear-gradient(to right, #1f515d, #83b6c3)',
                }}
            ></div>
        </div>
        <span className="text-xs text-gray-500">{ageRange}</span>
    </div>
)

const ROUTE_TYPES = {
    ALL: null,
    DOMESTIC: 'DMC',
    INTERNATIONAL: 'INL',
}

export default function DashboardExecutive() {
    const { t } = useTranslation()

    const StatProgressBar = ({ label, percentage = 0, range, iconBg, iconColor, Icon }) => (
        <div className="flex items-center gap-4 mb-4">
            <div className={`${iconBg} p-2 rounded-lg`}>
                <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div className="flex-1">
                <div className="flex justify-between mb-1">
                    <div>
                        <span className="text-gray-800 font-medium">{label}</span>
                        <span className="text-gray-400 text-sm ml-2">{range}</span>
                    </div>
                    <span className="text-gray-600 text-md font-bold">{percentage}%</span>
                </div>
                <div className="w-full h-[12px] bg-gray-100 rounded-full">
                    <div
                        className="h-full rounded-full"
                        style={{
                            width: `${percentage}%`,
                            background:
                                label === 'Young-Adults' ? '#caa72b' : 'linear-gradient(to right, #5d8e9a, #83b6c3)',
                        }}
                    ></div>
                </div>
            </div>
        </div>
    )

    const RouteProgress = ({ route, value }) => (
        <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
                <span className="text-primary text-sm font-medium">{route}</span>
                <span className="text-gray-600 whitespace-nowrap !text-[16px] font-bold">{value}</span>
            </div>
            <div className="w-full h-[3px] bg-gray-100 rounded-full overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-[#5d8e9a] to-[#83b6c3] rounded-full"
                    style={{ width: `${(value / 120) * 100}%` }}
                ></div>
            </div>
        </div>
    )

    const DEMOGRAPHICS_CONFIG = [
        {
            label: t('Children'),
            key: 'child_percentage',
            range: t('Under-18'),
            iconBg: 'bg-primary/40',
            iconColor: 'text-primary',
            Icon: UserIcon,
        },
        {
            label: t('Young-Adults'),
            key: 'infant_percentage',
            range: '18-30',
            iconBg: 'bg-[#d4b242]/30',
            iconColor: 'text-[#d4b242]',
            Icon: UserCircleIcon,
        },
        {
            label: t('Adults'),
            key: 'adult_percentage',
            range: '31-50',
            iconBg: 'bg-primary/20',
            iconColor: 'text-primary',
            Icon: UsersIcon,
        },
        {
            label: t('Seniors'),
            key: 'senior_percentage',
            range: '51+',
            iconBg: 'bg-primary/5',
            iconColor: 'text-primary/40',
            Icon: UserGroupIcon,
        },
    ]

    const [dashboardData, setDashboardData] = useState(null)
    const [selectedRouteType, setSelectedRouteType] = useState(ROUTE_TYPES.ALL)
    const [currentDateFilter, setCurrentDateFilter] = useState({ type: DATE_RANGES.TODAY, from: null, to: null })
    const stationRevenueChartRef = useRef(null)
    const bookingDistChartRef = useRef(null)
    const passengerDistChartRef = useRef(null)
    const tripsPerStationChartRef = useRef(null)
    const hourlyChartRef = useRef(null)

    const [getExecutiveDashboard, { loading }] = useLazyQuery(GET_EXECUTIVE_DASHBOARD, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getExecutiveDashboard?.success) {
                setDashboardData(data.getExecutiveDashboard.executive_dashboard)
                updateCharts(data.getExecutiveDashboarzd.executive_dashboard)
            }
        },
    })

    const handleDateChange = ({ type, from, to }) => {
        setCurrentDateFilter({ type, from, to })
        if (type === 'CUSTOM_RANGE') {
            getExecutiveDashboard({
                variables: {
                    date_range: {
                        start_date: from,
                        end_date: to,
                    },
                    route_type: selectedRouteType,
                },
            })
        } else {
            getExecutiveDashboard({
                variables: {
                    period: type,
                    route_type: selectedRouteType,
                },
            })
        }
    }

    const handleRouteTypeChange = routeType => {
        setSelectedRouteType(routeType)
        const { type, from, to } = currentDateFilter
        if (type === 'CUSTOM_RANGE') {
            getExecutiveDashboard({
                variables: {
                    date_range: {
                        start_date: from,
                        end_date: to,
                    },
                    route_type: routeType,
                },
            })
        } else {
            getExecutiveDashboard({
                variables: {
                    period: type,
                    route_type: routeType,
                },
            })
        }
    }

    const updateCharts = data => {
        const resizeHandlers = []
        // Revenue per Station Chart (with Start and End Net Revenue columns)
        if (data?.station_details?.length) {
            const revenueStationChart = () => {
                if (!stationRevenueChartRef.current) {
                    setTimeout(revenueStationChart, 100)
                    return
                }

                echarts.dispose(stationRevenueChartRef.current)
                const chart = echarts.init(stationRevenueChartRef.current)

                const stationNames = data.station_details.map(
                    station => station?.station_name?.[i18next?.language || 'en'] || 'N/A'
                )

                const passengers = data.station_details.map(station => station?.passengers_count ?? 0)

                const revenue = data.station_details.map(station => station?.revenue ?? 0)

                const trips = data.station_details.map(station => station?.trip_count ?? 0)

                const option = {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                        formatter: params => {
                            return params
                                .map(p => {
                                    const val = p.seriesName.includes('Revenue')
                                        ? `BHD ${Number(p.value).toFixed(2)}`
                                        : Number(p.value).toLocaleString()
                                    return `${p.seriesName}: ${val}`
                                })
                                .join('<br/>')
                        },
                    },

                    legend: {
                        top: '0%',
                        left: 'center',
                        textStyle: {
                            color: '#374151',
                            fontSize: 13,
                            fontWeight: 500,
                        },
                        itemWidth: 10,
                        itemHeight: 10,
                        itemGap: 20,
                    },

                    grid: {
                        left: '3%',
                        right: '3%',
                        bottom: '16%',
                        top: '18%',
                        containLabel: true,
                    },

                    xAxis: {
                        type: 'category',
                        data: stationNames.map(name => name.split(' ').join('\n')),
                        axisLabel: {
                            color: '#374151',
                            interval: 0,
                            fontSize: 12,
                        },
                        axisLine: {
                            lineStyle: { color: '#e5e7eb' },
                        },
                    },

                    yAxis: {
                        type: 'value',
                        axisLabel: {
                            color: '#374151',
                            fontSize: 12,
                        },
                        splitLine: {
                            show: false,
                        },
                    },

                    series: [
                        {
                            name: 'Passengers',
                            type: 'bar',
                            data: passengers,
                            barWidth: '25%',

                            label: {
                                show: true,
                                position: 'top',
                                color: '#0c505b',
                                fontWeight: 700,
                                fontSize: 12,
                                formatter: ({ value }) => value ?? 0,
                            },

                            itemStyle: {
                                borderRadius: [6, 6, 0, 0],
                                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    { offset: 0, color: '#0c505b' },
                                    { offset: 1, color: '#0c505b' },
                                ]),
                            },
                        },
                        {
                            name: 'Revenue (BHD)',
                            type: 'bar',
                            data: revenue,
                            barWidth: '25%',

                            label: {
                                show: true,
                                position: 'top',
                                color: '#caa72b',
                                fontWeight: 700,
                                fontSize: 12,
                                formatter: ({ value }) => `BHD ${Number(value ?? 0).toFixed(2)}`,
                            },

                            itemStyle: {
                                borderRadius: [6, 6, 0, 0],
                                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    { offset: 0, color: '#caa72b' },
                                    { offset: 1, color: '#caa72b' },
                                ]),
                            },
                        },
                        {
                            name: 'Trip Count',
                            type: 'bar',
                            data: trips,
                            barWidth: '25%',

                            label: {
                                show: true,
                                position: 'top',
                                color: '#6b7280',
                                fontWeight: 700,
                                fontSize: 12,
                                formatter: ({ value }) => value ?? 0,
                            },

                            itemStyle: {
                                borderRadius: [6, 6, 0, 0],
                                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    { offset: 0, color: '#9ca3af' },
                                    { offset: 1, color: '#9ca3af' },
                                ]),
                            },
                        },
                    ],
                }

                chart.setOption(option)

                const handleResize = () => chart.resize()
                window.addEventListener('resize', handleResize)

                return () => {
                    window.removeEventListener('resize', handleResize)
                    chart.dispose()
                }
            }

            revenueStationChart()
        }
        // Hourly Booking Trends (dashboard style)
        if (data?.hourly_booking_trends?.length && hourlyChartRef.current) {
            const chart = echarts.init(hourlyChartRef.current)
            const option = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                },
                grid: {
                    left: '1%',
                    right: '1%',
                    bottom: '2%',
                    top: '2%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: data.hourly_booking_trends.map(item => item.booking_hour_label.split(' ').join('\n')),
                    axisLabel: {
                        color: '#374151',
                        interval: 0,
                        formatter: function (value) {
                            return value.split('\n').join('\n')
                        },
                        lineHeight: 15,
                        align: 'center',
                    },
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: '#edf2f7',
                        },
                    },
                },
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            color: '#374151',
                        },
                        splitLine: {
                            show: true,
                            lineStyle: {
                                color: '#edf2f7',
                            },
                        },
                        axisLine: {
                            show: true,
                            lineStyle: {
                                color: '#edf2f7',
                            },
                        },
                    },
                ],
                series: [
                    {
                        data: data.hourly_booking_trends.map(item => item.booking_count),
                        type: 'bar',
                        barWidth: '60%',
                        itemStyle: {
                            color: '#0c505b',
                            borderRadius: [3, 3, 0, 0],
                            shadowColor: 'rgba(0, 0, 0, 0.1)',
                            shadowBlur: 5,
                        },
                        label: {
                            show: true,
                            position: 'insideBottom',
                            color: '#ffffff',
                            fontSize: 12,
                            rotate: 90,
                            distance: 15,
                            formatter: function (params) {
                                return `${params.value}`
                            },
                            verticalAlign: 'middle',
                            textBorderColor: '#0c505b',
                            textBorderWidth: 2,
                            textShadowBlur: 2,
                        },
                        emphasis: {
                            itemStyle: {
                                color: '#6fa0ad',
                            },
                        },
                    },
                ],
            }
            chart.setOption(option)
            window.addEventListener('resize', () => chart.resize())
            resizeHandlers.push(() => chart.dispose())
        }

        // Booking Distribution
        if (data?.booking_distribution?.length && bookingDistChartRef.current) {
            const chart = echarts.init(bookingDistChartRef.current)
            const option = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: params => {
                        return params.map(p => `${p.seriesName}: ${p.value}`).join('<br/>')
                    },
                },

                grid: {
                    left: '3%',
                    right: '3%',
                    bottom: '16%',
                    top: '18%',
                    containLabel: true,
                },

                xAxis: {
                    type: 'category',
                    data: data.booking_distribution.map(p => p.period_label),
                    axisLabel: { color: '#374151', interval: 'auto', fontSize: 12 },
                    axisLine: { lineStyle: { color: '#e5e7eb' } },
                },

                yAxis: {
                    type: 'value',
                    axisLabel: { color: '#374151', fontSize: 12 },
                    splitLine: { show: false },
                },

                series: [
                    {
                        name: 'Daypass',
                        type: 'bar',
                        data: data.booking_distribution.map(p => p.daypass_count),
                        barWidth: '30%',

                        label: {
                            show: true,
                            position: 'top',
                            color: '#caa72b',
                            fontWeight: 700,
                            fontSize: 12,
                            formatter: ({ value }) => value ?? 0, // handles null safely
                        },

                        itemStyle: {
                            borderRadius: [6, 6, 0, 0],
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: '#caa72b' },
                                { offset: 1, color: '#caa72b' },
                            ]),
                        },
                    },
                    {
                        name: 'Standard',
                        type: 'bar',
                        data: data.booking_distribution.map(p => p.std_booking_count),
                        barWidth: '30%',

                        label: {
                            show: true,
                            position: 'top',
                            color: '#0c505b',
                            fontWeight: 700,
                            fontSize: 12,
                            formatter: ({ value }) => value ?? 0,
                        },

                        itemStyle: {
                            borderRadius: [6, 6, 0, 0],
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: '#0c505b' },
                                { offset: 1, color: '#0c505b' },
                            ]),
                        },
                    },
                ],
            }

            chart.setOption(option)
            const resize = () => chart.resize()
            window.addEventListener('resize', resize)
            resizeHandlers.push(() => {
                window.removeEventListener('resize', resize)
                chart.dispose()
            })
        }

        // Passenger Distribution
        if (data?.passengers_distribution?.length && passengerDistChartRef.current) {
            const chart = echarts.init(passengerDistChartRef.current)
            const option = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: params => `${params[0].seriesName}: ${params[0].value}`,
                },
                grid: {
                    left: '3%',
                    right: '3%',
                    bottom: '16%',
                    top: '18%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: data.passengers_distribution.map(p => p.period_label),
                    axisLabel: { color: '#374151', interval: 'auto', fontSize: 12 },
                    axisLine: { lineStyle: { color: '#e5e7eb' } },
                },
                yAxis: {
                    type: 'value',
                    axisLabel: { color: '#374151', fontSize: 12 },
                    splitLine: { show: false },
                },
                label: {
                    show: true,
                    position: 'top',
                    color: '#0c505b',
                    fontWeight: 700,
                    fontSize: 12,
                    formatter: ({ value }) => value ?? 0,
                },
                series: [
                    {
                        name: 'Passengers',
                        type: 'bar',
                        data: data.passengers_distribution.map(p => p.passengers_count),
                        barWidth: '30%',
                        barCategoryGap: '85%',
                        itemStyle: {
                            borderRadius: [6, 6, 0, 0],
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: '#0c505b' },
                                { offset: 1, color: '#0c505b' },
                            ]),
                        },
                    },
                ],
            }
            chart.setOption(option)
            const resize = () => chart.resize()
            window.addEventListener('resize', resize)
            resizeHandlers.push(() => {
                window.removeEventListener('resize', resize)
                chart.dispose()
            })

            return () => resizeHandlers.forEach(clean => clean())
        }
    }

    const handleDownloadPDF = () => {
        const element = document.getElementById('dashboard-pdf-content')
        if (!element) return
        // Add force-print class to enable print styles
        element.classList.add('force-print')
        html2pdf()
            .set({
                margin: 0.5,
                filename: 'executive_dashboard.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
            })
            .from(element)
            .save()
            .then(() => {
                element.classList.remove('force-print')
            })
            .catch(() => {
                element.classList.remove('force-print')
            })
    }

    useEffect(() => {
        getExecutiveDashboard({
            variables: {
                period: DATE_RANGES.TODAY,
                route_type: null,
            },
        })
    }, [])

    useEffect(() => {
        if (
            dashboardData &&
            (stationRevenueChartRef.current || bookingDistChartRef.current || passengerDistChartRef.current)
        ) {
            updateCharts(dashboardData)
        }
    }, [dashboardData])

    useEffect(() => {
        if (
            dashboardData &&
            (stationRevenueChartRef.current || bookingDistChartRef.current || passengerDistChartRef.current)
        ) {
            updateCharts(dashboardData)
        }
    }, [i18next.language])
    console.log(dashboardData)
    return (
        <div className="w-full">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6 print:flex-col print:items-stretch print:gap-2 print:mb-2">
                <h1 className="text-2xl font-bold text-gray-800 w-full md:w-auto text-center md:text-left print:text-lg print:font-bold">
                    {t('Executive Overview')}
                </h1>
                <div className="flex gap-2 w-full md:w-auto print:w-full print:gap-1">
                    <div className="w-[150px] print:hidden">
                        <Select
                            value={selectedRouteType || ''}
                            onChange={e => handleRouteTypeChange(e.target.value || null)}
                            className="w-full"
                        >
                            <option value="">{t('All Routes')}</option>
                            <option value={ROUTE_TYPES.DOMESTIC}>{t('Domestic')}</option>
                            <option value={ROUTE_TYPES.INTERNATIONAL}>{t('International')}</option>
                        </Select>
                    </div>
                    <DateFilter onDateChange={handleDateChange} className="w-full md:w-auto print:hidden" />
                    <button
                        onClick={handleDownloadPDF}
                        className="bg-primary text-white px-3 py-1 text-sm rounded-lg shadow hover:bg-primary/80 transition flex items-center gap-2 print:hidden"
                    >
                        <PrinterIcon className="w-4 h-4" />
                        {t('Download PDF')}
                    </button>
                </div>
            </div>
            <div id="dashboard-pdf-content" className="print:bg-white print:p-2">
                {loading ? (
                    <BookingsDashboardSkeleton />
                ) : (
                    <>
                        <div className="mb-8 relative">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 print:grid-cols-3 print:gap-2 print:mb-2">
                                <StatCard
                                    title={t('Total Bookings')}
                                    value={dashboardData?.total_bookings || 0}
                                    icon={<TicketIcon className="w-5 h-5 text-primary" />}
                                    iconBg="bg-primary/10"
                                />
                                <StatCard
                                    title={t('Standard Bookings')}
                                    value={dashboardData?.total_std_bookings || 0}
                                    icon={<img src={stSeat} className="w-5 h-5" alt="Standard" />}
                                    iconBg="bg-primary/10"
                                />
                                <StatCard
                                    title={t('Day Pass Booking')}
                                    value={dashboardData?.total_daypass_bookings || 0}
                                    icon={<TicketIcon className="w-5 h-5 text-primary/90" />}
                                    iconBg="bg-primary/10"
                                />
                                <StatCard
                                    title={t('Total Passenger')}
                                    value={dashboardData?.total_passengers || 0}
                                    icon={<UsersIcon className="w-5 h-5 text-primary/90" />}
                                    iconBg="bg-primary/10"
                                />
                            </div>
                        </div>
                        <br />

                        <div className="flex flex-col lg:flex-row gap-4 w-full">
                            {/*  Booking Distribution  */}
                            <div className="bgwhite p-3 rounded-xl shadow-lg border border-gray-100 w-full lg:w-1/2 print:w-1/2">
                                <h2 className="text-lg font-semibold mb-3">{t('Booking Distribution')}</h2>

                                {!dashboardData?.booking_distribution?.length ? (
                                    <GraphQlResponseNoData className="!py-8 !mt-0" />
                                ) : (
                                    <>
                                        <div className="w-full">
                                            <div ref={bookingDistChartRef} style={{ height: '250px' }}></div>
                                        </div>

                                        <div className="space-y-3">
                                            {/* Daypass */}
                                            <div className="flex justify-between items-center px-4">
                                                <div className="flex items-center gap-x-2">
                                                    <div className="bg-[#d4b242]/20 rounded-lg p-2">
                                                        <img src={vipSeat} alt="Daypass" className="w-4 h-4" />
                                                    </div>
                                                    <p className="font-semibold text-sm">{t('Daypass')}</p>
                                                </div>
                                                <p className="font-extrabold text-[#d4b242]">
                                                    {dashboardData.booking_distribution.reduce(
                                                        (acc, item) => acc + item.daypass_count,
                                                        0
                                                    )}
                                                </p>
                                            </div>

                                            {/* Standard */}
                                            <div className="flex justify-between items-center px-4">
                                                <div className="flex items-center gap-x-2">
                                                    <div className="bg-[#6fa0ad]/20 rounded-lg p-2">
                                                        <img src={stSeat} alt="Standard" className="w-4 h-4" />
                                                    </div>
                                                    <p className="font-semibold text-sm">{t('Standard')}</p>
                                                </div>
                                                <p className="font-extrabold text-[#6fa0ad]">
                                                    {dashboardData.booking_distribution.reduce(
                                                        (acc, item) => acc + item.std_booking_count,
                                                        0
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/*  Passenger Distribution  */}
                            <div className="bgwhite p-3 rounded-xl shadow-lg border border-gray-100 w-full lg:w-1/2 print:w-1/2">
                                <h2 className="text-lg font-semibold mb-3">{t('Passenger Distribution')}</h2>

                                {!dashboardData?.passengers_distribution?.length ? (
                                    <GraphQlResponseNoData className="!py-8 !mt-0" />
                                ) : (
                                    <>
                                        <div className="w-full">
                                            <div ref={passengerDistChartRef} style={{ height: '250px' }}></div>
                                        </div>

                                        <div className="flex justify-between items-center px-4">
                                            <div className="flex items-center gap-x-2">
                                                <div className="bg-[#6fa0ad]/20 rounded-lg p-2">
                                                    <UsersIcon
                                                        color="#6fa0ad"
                                                        alt="Total Passengers"
                                                        className="w-4 h-4"
                                                    />
                                                </div>
                                                <p className="font-semibold text-sm">{t('Total Passengers')}</p>
                                            </div>
                                            <p className="font-extrabold text-[#6fa0ad]">
                                                {dashboardData.passengers_distribution.reduce(
                                                    (acc, item) => acc + item.passengers_count,
                                                    0
                                                )}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <br />
                        <br />

                        <div className="mb-8 relative">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 print:grid-cols-3 print:gap-2 print:mb-2">
                                <StatCard
                                    title={t('Scheduled Trips')}
                                    value={dashboardData?.total_scheduled_trips || 0}
                                    icon={<UsersIcon className="w-5 h-5 text-primary/90" />}
                                    iconBg="bg-primary/10"
                                />
                                <StatCard
                                    title={t('Trips Completed')}
                                    value={dashboardData?.trips_completed || 0}
                                    icon={<CheckCircleIcon className="w-5 h-5 text-green/90" />}
                                    iconBg="bg-green/10"
                                />
                                <StatCard
                                    title={t('Trips Cancelled')}
                                    value={dashboardData?.trips_cancelled || 0}
                                    icon={<XCircleIcon className="w-5 h-5 text-[#8e584f]" />}
                                    iconBg="bg-[#fae6eb]"
                                />
                                <StatCard
                                    title={t('Complaint Rate')}
                                    value={dashboardData?.complaint_rate || 0}
                                    icon={<DocumentTextIcon className="w-5 h-5 text-[#8e584f]" />}
                                    iconBg="bg-[#fae6eb]"
                                />
                                <StatCard
                                    title={t('Customer Ratings')}
                                    value={dashboardData?.customer_rating || 0}
                                    icon={<StarIcon className="w-5 h-5 text-yellow/90" />}
                                    iconBg="bg-yellow/10"
                                />
                            </div>
                        </div>
                        <br />

                        <div className="flex w-full gap-4 print:flex-col print:gap-2">
                            {/* Most Popular Routes by Passengers */}
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-[50%] print:w-full flex flex-col">
                                <h2 className="text-lg font-semibold mb-4">
                                    {t('Most Popular Routes (by Passengers)')}
                                </h2>
                                <div className="overflow-y-auto pr-2 flex-1">
                                    <div className="space-y-4">
                                        {dashboardData?.top_routes_by_bookings?.length ? (
                                            dashboardData.top_routes_by_bookings.map((route, idx) => (
                                                <RouteProgress
                                                    key={route.master_route_id || idx}
                                                    route={route.route_name?.[i18next.language || 'en']}
                                                    value={route.pass_count || 0}
                                                />
                                            ))
                                        ) : (
                                            <GraphQlResponseNoData className="!py-10 !mt-0" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right side: Routes and Passenger Types stacked */}
                            <div className="flex flex-col gap-4 w-[50%] print:w-full">
                                {/* Routes */}
                                <div className="relative bg-white rounded-xl shadow border border-gray-100 p-4 pt-6 w-full">
                                    <div className="absolute top-0 left-0 w-full border-t border-gray-200"></div>
                                    <h2 className="absolute -top-3 left-4 bg-white px-3 text-lg font-semibold text-gray-800">
                                        {t('Routes')}
                                    </h2>
                                    <div className="flex flex-col gap-4 mt-4">
                                        <StatCard
                                            title={
                                                t('Most Demanding Routes') +
                                                ' - ' +
                                                dashboardData?.highest_demand_route?.route_name?.en
                                            }
                                            value={`${dashboardData?.highest_demand_route?.value || 0} ${t('Bookings')}`}
                                            icon={<BoatIcon className="w-5 h-5" />}
                                            iconBg="bg-primary/10"
                                        />
                                        <StatCard
                                            title={
                                                t('Least Demanding Routes') +
                                                ' - ' +
                                                dashboardData?.lowest_demand_route?.route_name?.en
                                            }
                                            value={`${dashboardData?.lowest_demand_route?.value || 0} ${t('Bookings')}`}
                                            icon={<BoatIcon className="w-5 h-5" />}
                                            iconBg="bg-primary/10"
                                        />
                                        <StatCard
                                            title={
                                                t('Most Profitable Routes') +
                                                ' - ' +
                                                dashboardData?.most_profit_route?.route_name?.en
                                            }
                                            value={`BHD ${Number(dashboardData?.most_profit_route?.value || 0).toFixed(2)}`}
                                            icon={<BoatIcon className="w-5 h-5" />}
                                            iconBg="bg-primary/10"
                                        />
                                    </div>
                                </div>
                                {/* Passenger Types */}
                                <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full">
                                    <h2 className="text-lg font-semibold mb-4">{t('Passenger Types')}</h2>
                                    {!dashboardData?.passenger_type_counts ? (
                                        <GraphQlResponseNoData className="!py-10 !mt-0" />
                                    ) : (
                                        <PassengerTypeChart
                                            passengerTypeCounts={dashboardData?.passenger_type_counts}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <br />

                        <div className="bgwhite p-4 mt-6 rounded-xl shadow-lg border border-gray-100 w-full mb-6 print:w-full">
                            <h2 className="text-lg font-semibold mb-4">{t('Hourly Booking Trends')}</h2>
                            <div className="overflow-x-auto">
                                <div className="min-w-[1200px] lg:min-w-full">
                                    <div ref={hourlyChartRef} style={{ height: '320px' }}></div>
                                </div>
                            </div>
                        </div>
                        <br />
                        <h2 className="text-lg font-semibold mb-3">{t('Financial Summary')}</h2>
                        <br />
                        <div id="dashboard-pdf-content" className="print:bg-white print:p-2">
                            {loading ? (
                                <DashboardSkeleton />
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6 print:grid-cols-3 print:gap-2 print:mb-2">
                                        <StatCard
                                            title={t('Net Revenue')}
                                            value={`BHD ${dashboardData?.net_revenue || 0}`}
                                            type="net_revenue"
                                            icon={<BanknotesIcon className="w-5 h-5 text-[#caa72b]" />}
                                            iconBg="bg-[#f6e7c1]"
                                        />
                                        <StatCard
                                            title={t('Day-Pass Net Revenue')}
                                            value={`BHD ${dashboardData?.day_pass_net_revenue || 0}`}
                                            type="day_pass_net_revenue"
                                            icon={<BanknotesIcon className="w-5 h-5 text-[#caa72b]" />}
                                            iconBg="bg-[#f6e7c1]"
                                        />
                                        <StatCard
                                            title={t('Standard Seat Revenue')}
                                            value={`BHD ${dashboardData?.gross_std_seat_revenue || 0}`}
                                            type="stdSeatRevenue"
                                            icon={<img src={stSeat} className="w-[20px] " alt="Standard" />}
                                            iconBg="bg-[#6fa0ad]/20"
                                        />
                                        <StatCard
                                            title={t('VIP Seat Revenue')}
                                            value={`BHD ${dashboardData?.gross_vip_seat_revenue || 0}`}
                                            type="vipSeatRevenue"
                                            icon={<img src={vipSeat} className="w-[20px]" alt="VIP" />}
                                            iconBg="bg-[#d4b242]/30"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="w-full mb-8 flex flex-col lg:flex-row gap-4 print:flex-col print:gap-2">
                            {/* Revenue per Station */}
                            <div className="w-full lg:w-[60%]">
                                <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full flex flex-col h-[350px]">
                                    <h2 className="text-lg font-semibold mb-4">{t('Revenue per Station')}</h2>
                                    <div className="flex-1">
                                        {!dashboardData?.station_details?.length ? (
                                            <GraphQlResponseNoData className="!py-10 !mt-0" />
                                        ) : (
                                            <div className="overflow-x-auto h-full">
                                                <div className="w-full h-full">
                                                    <div ref={stationRevenueChartRef} style={{ height: '320px' }} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Payment Methods */}
                            <div className="bgwhite p-4 rounded-xl shadow-lg border border-gray-100 w-full lg:w-[40%] print:w-full">
                                <h2 className="text-lg font-semibold mb-4">{t('Payment Methods')}</h2>
                                {!dashboardData?.payment_method_revenues?.length ? (
                                    <GraphQlResponseNoData className="!py-10 !mt-0" />
                                ) : (
                                    <PaymentMethodsChart paymentData={dashboardData?.payment_method_revenues} />
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
